import React, { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import GenericAutocomplete, {
  type GenericAutocompleteProps
} from '../FormAutoComplete'
import api from '../../../utils/api'

interface ApiAutoCompleteProps<T extends Record<string, any>>
  extends Omit<
  GenericAutocompleteProps<T>,
  'getOptionLabel' | 'getOptionValue' | 'loading' | 'onChange' | 'options'
  > {
  selectedItem?: T
  apiEndpoint: string
  labelField: string
  valueField?: string
  WholeValue?: boolean
  onChange?: (value: any) => void
  apiParams?: Record<string, any>
  clearIcon?: React.ReactNode
  skip?: boolean
}

export const ApiAutoComplete = <T extends Record<string, any>>({
  apiEndpoint,
  labelField,
  valueField: paramValueField,
  WholeValue,
  onChange,
  apiParams,
  clearIcon,
  skip = false,
  selectedItem,
  ...props
}: ApiAutoCompleteProps<T>): JSX.Element => {
  const valueField = paramValueField ?? props.name
  const [input, setInput] = useState<string>('')

  const reverseCamelCase = (str: string): string => str.replace(/([A-Z])/g, '_$1').toLowerCase()
  const labelFieldUnderscore = reverseCamelCase(labelField)

  const { data: rawOptions = [], isLoading } = useQuery<T[]>({
    queryKey: [apiEndpoint, input],
    enabled: !skip,
    staleTime: skip ? 0 : 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await api.get(apiEndpoint, {
        params: {
          pageSize: 10,
          search: input,
          sort: JSON.stringify({ field: labelFieldUnderscore, direction: 'asc' }),
          ...apiParams
        }
      })
      return data
    }
  })

  const mergedOptions = useMemo(() => {
    if (skip) {
      return selectedItem ? [selectedItem] : []
    }
    if (!selectedItem) return rawOptions

    const isItemPresent = rawOptions.some(
      item => item[valueField] === selectedItem[valueField]
    )
    return isItemPresent
      ? rawOptions
      : [selectedItem, ...rawOptions]
  }, [skip, rawOptions, selectedItem, valueField])

  const computedSelectedValue = selectedItem
    ? WholeValue
      ? selectedItem
      : selectedItem[valueField]
    : null

  return (
    <GenericAutocomplete
      {...props}
      options={mergedOptions}
      selectedValue={computedSelectedValue}
      getOptionLabel={option => String((option as any)[labelField])}
      getOptionValue={(option: any) => option?.[valueField]}
      onInputChange={value => { setInput(value) }}
      loading={isLoading}
      onChange={onChange}
      clearIcon={clearIcon}
    />
  )
}
